import { useEffect, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { Drawer } from '@material-ui/core';
import { useQueryClient } from '@tanstack/react-query';
import { showQuestionDetailsAction } from 'actions/questionDetails';
import { useMatchedRoute } from 'components/hooks/useMatchedRoute';
import usePrevious from 'components/usePrevious';
import ReviewQuestion, { AnswerOption } from 'models/reviewQuestion';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { CircularLoader } from 'screens/analytics/commonComponents';
import { selectQuestionDetails } from 'selectors/questionDetails';
import styled from 'styled-components';

import { NeedAttention } from 'assets/assetsV1/iconComponents';

import {
  ENGAGEMENT_ANSWER_TYPES,
  QUESTION_FORM_SOURCES,
  QUESTION_TYPES,
  REVIEW_ANSWER_TYPE_MAP,
} from 'constants/ProjectConstants';
import { getQueryParams } from 'constants/helper';
import { ONE_ON_ONE_EVENTS_OLD, sendOneOnOneEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getOrganizationTags } from 'services/admin';
import { hideQuestionDetails } from 'services/questionDetails';
import { getQuestionById, saveQuestion } from 'services/reviews';
import { showErrorSnackbar } from 'services/snackbar';

import { TextLabel } from 'uiToolkitV1/Typography';

import QuestionForm, { MAX_CHARACTER_LIMIT_FOR_QUESTION } from './questionForm';
import SidepanelFooter from './sidepanelFooter';
import SidepanelHeader from './sidepanelHeader';

const QUESTION_SOURCES = {
  REVIEW: 'review',
  ONE_ON_ONE: 'one_on_one',
  ENGAGEMENT: 'engagement',
};

const ACTIONS = {
  CREATE: 'create',
  EDIT: 'edit',
  COPY: 'copy',
};

const sendTrackingEvent = (source, action, pageName) => {
  let eventName;
  switch (source) {
    case QUESTION_SOURCES.ONE_ON_ONE:
      eventName =
        action === ACTIONS.EDIT ? ONE_ON_ONE_EVENTS_OLD.updatedQuestion : ONE_ON_ONE_EVENTS_OLD.createdNewQuestion;
      break;
    default:
      break;
  }
  // Send Mark Complete or revertComplete
  eventName &&
    sendOneOnOneEvent({
      eventName,
      page: {
        pageName,
        flow: 'Activity',
      },
    });
};

const useQuestionLinkState = ({ dispatch, display, updatedQuestion }) => {
  const location = useLocation();
  const queryString = location.search;
  const currentPath = location.pathname;
  const previousQueryString = usePrevious(queryString);

  useEffect(() => {
    const getDetailsFromRoute = () => {
      const { showQuestionDetails, action = ACTIONS.EDIT, source, questionId } = getQueryParams(queryString);
      const areQuestionDetailRouteAndQuestionIdPresent = showQuestionDetails === 'true' && questionId;
      return { areQuestionDetailRouteAndQuestionIdPresent, questionId, action, source };
    };
    if (queryString === previousQueryString) return;
    const { questionId, areQuestionDetailRouteAndQuestionIdPresent, action, source } = getDetailsFromRoute();
    if (areQuestionDetailRouteAndQuestionIdPresent) {
      dispatch(showQuestionDetailsAction({ questionId, action, source }));
    } else {
      // if (display) dispatch(hideQuestionDetailsAction({ updatedQuestion }));
    }
  }, [queryString, previousQueryString, currentPath, dispatch, display]);
};

const QuestionDetailsSidePanel = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(null);
  const [question, setQuestion] = useState({});
  const [competencies, setCompetencies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [isInvalidQuestionDescription, setInvalidQuestionDescription] = useState(false);
  const [isInvalidVariantQuestionDescription, setInvalidVariantQuestionDescription] = useState(false);
  const {
    questionId = 0,
    action = ACTIONS.EDIT,
    display,
    source = 'review',
    updatedQuestion,
  } = useSelector(selectQuestionDetails, shallowEqual);
  const questionTypes = QUESTION_TYPES[source];
  useQuestionLinkState({ dispatch, display, updatedQuestion });
  const queryClient = useQueryClient();
  const { pageName } = useMatchedRoute();

  const isDuplicating = action === ACTIONS.COPY;

  const refetchHandler = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewQuestionList.activeQuestions]);
    queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewQuestionList.archivedQuestions]);
  };

  const getCompetenciesList = () => {
    getOrganizationTags({ teamId: -1, tagType: 'CORE_COMPETENCIES' })
      .then((orgTags) => {
        setCompetencies(orgTags.filter((tag) => tag.active));
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message)))
      .finally(() => {
        setLoading(false);
      });
  };

  const getDriversList = () => {
    getOrganizationTags({ teamId: -1, tagType: 'ENGAGEMENT_DRIVER' })
      .then((orgTags) => {
        setDrivers(orgTags.filter((tag) => tag.active));
      })
      .catch((err) => dispatch(showErrorSnackbar(err.message)))
      .finally(() => {
        setLoading(false);
      });
  };

  const getQuestionDetails = (questionId, action) => {
    setLoading(true);
    getQuestionById({ questionId })
      .then((res) => {
        const q = { ...res };
        if (action === ACTIONS.COPY) {
          delete q.id;
        }
        if (q.answerOptions.length === 0) {
          q.answerOptions = [];
        }
        setQuestion(q);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Sorry, unable to fetch the question details!'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (questionId && competencies.length === 0) {
      getCompetenciesList();
      getDriversList();
    }
  }, [questionId]);

  useEffect(() => {
    if (
      questionId &&
      !Number.isNaN(questionId) &&
      questionId > 0 &&
      (action === ACTIONS.EDIT || action === ACTIONS.COPY)
    ) {
      getQuestionDetails(questionId, action);
    } else {
      const newQuestion = ReviewQuestion.fromJSON({});
      newQuestion.answerType = source === QUESTION_FORM_SOURCES.ONE_ON_ONE ? 'COMMENT' : 'DROP_DOWN';
      newQuestion.questionType =
        source === 'review' ? 'QNA' : source === QUESTION_FORM_SOURCES.ONE_ON_ONE ? 'TALKING_POINT' : 'ENGAGEMENT';
      newQuestion.answerOptions = [];
      setQuestion(newQuestion);
    }
  }, [questionId]);

  const handleChange = (param, value) => {
    const isAnswerTypeChanged = param === 'answerType' && question.answerType !== value;
    // resetDataVar for the component to know when answer type has changes, so that it can resetData,
    // this is only needed when editing question
    let updatedQuestion = {
      ...question,
      ...(isAnswerTypeChanged && { resetDataVar: (question.resetDataVar ?? 0) + 1 }),
    };
    // if answer type is changed update all additional option values to default value, after that they can be set again as per question type conditions below
    if (isAnswerTypeChanged) {
      updatedQuestion.enableFeedbackText = false;
      updatedQuestion.allowRange = false;
      updatedQuestion.multiSelect = false;
      updatedQuestion.isTextRequired = false;
    }
    if (param === 'NUMBER_RATING' || param === 'STAR_RATING') {
      updatedQuestion.maxOptions = value.maxOptions;
      updatedQuestion.answerOptions = value.answerOptions;
    } else {
      updatedQuestion = { ...updatedQuestion, [param]: value };
    }
    if (param === 'answerType') {
      if (source === QUESTION_FORM_SOURCES.ONE_ON_ONE) {
        if (value === 'EMOJI') {
          updatedQuestion.answerOptions = [];
          for (let i = 0; i < 5; i += 1) {
            updatedQuestion.answerOptions.push(
              AnswerOption.fromJSON({ score: i + 1, description: i + 1, displayOrder: i + 1 })
            );
          }
          updatedQuestion.questionType = 'EMOTION';
        } else {
          // answerType: COMMENT
          updatedQuestion.questionType = 'TALKING_POINT';
        }
      } else if (
        ['DROP_DOWN', 'MULTI_SELECT'].includes(value) &&
        (!updatedQuestion.answerOptions ||
          updatedQuestion.answerOptions.length === 0 ||
          (['NUMBER_RATING', 'STAR_RATING'].includes(question.answerType) &&
            ['QNA', 'GOAL', 'COMPETENCY'].includes(question.questionType)))
      ) {
        updatedQuestion.answerOptions = [];
      } else if (
        (value === 'STAR_RATING' || value === 'NUMBER_RATING') &&
        ['QNA', 'GOAL', 'COMPETENCY'].includes(question.questionType)
      ) {
        updatedQuestion.answerOptions = [
          ...Array.from({ length: 5 }).map((i, index) => ({
            description: '',
            displayOrder: index + 1,
            score: index + 1,
          })),
        ];
      } else if ((value === 'NUMBER_RATING' || value === 'EMOJI') && question.questionType === 'ENGAGEMENT') {
        updatedQuestion.maxOptions = 5;
        updatedQuestion.answerOptions = [];
        for (let i = 0; i < 5; i += 1) {
          updatedQuestion.answerOptions.push(AnswerOption.fromJSON({ id: i + 1, score: i + 1 }));
        }
      } else {
        // Enables Text Answer
        if (value === REVIEW_ANSWER_TYPE_MAP.NONE || value === ENGAGEMENT_ANSWER_TYPES.NONE) {
          updatedQuestion.enableFeedbackText = true;
          updatedQuestion.isTextRequired = true;
        }
      }
    } else if (param === 'questionType') {
      const find = questionTypes.filter((q) => q.value === value)[0];
      if (find) {
        updatedQuestion.tagId = null;
      } else {
        updatedQuestion.questionType = 'COMPETENCY';
        updatedQuestion.tagId = value;
      }
    }

    if (param === 'description') {
      if (value.length > MAX_CHARACTER_LIMIT_FOR_QUESTION) {
        setInvalidQuestionDescription(true);
      } else {
        setInvalidQuestionDescription(false);
      }
    }
    if (param === 'providerToDescription') {
      const { MANAGER, SELF, PEER, UPWARD } = value;
      if (
        (MANAGER && MANAGER.length > MAX_CHARACTER_LIMIT_FOR_QUESTION) ||
        (SELF && SELF.length > MAX_CHARACTER_LIMIT_FOR_QUESTION) ||
        (PEER && PEER.length > MAX_CHARACTER_LIMIT_FOR_QUESTION) ||
        (UPWARD && UPWARD.length > MAX_CHARACTER_LIMIT_FOR_QUESTION)
      ) {
        setInvalidVariantQuestionDescription(true);
      } else {
        setInvalidVariantQuestionDescription(false);
      }
    }
    setQuestion(updatedQuestion);
  };

  const handleSave = () => {
    setLoading(true);
    setQuestion((prevState) => ({ ...prevState, hasCustomDescription: false }));
    sendTrackingEvent(source, action, pageName);

    // Some data manipulation is being performed in saveQuestion({}) service before calling API.
    saveQuestion({ question, source })
      .then((res) => {
        if (res) {
          refetchHandler();
          handleClose(ReviewQuestion.fromJSON(res.entity));
        } else {
          dispatch(showErrorSnackbar(res.message || 'Sorry, unable to save the question!'));
        }
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Sorry, unable to save the question!'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClose = (updatedQuestion) => {
    dispatch(
      hideQuestionDetails({
        updatedQuestion: updatedQuestion && updatedQuestion.id ? updatedQuestion : null,
        history,
      })
    );
  };

  const handleKeyDown = ({ keyCode }) => {
    if (keyCode === 27) handleClose();
  };

  const getIsDisabled = (source) => {
    switch (source) {
      case QUESTION_SOURCES.ONE_ON_ONE:
        return !question?.description?.length;
      case QUESTION_SOURCES.REVIEW:
        return action === ACTIONS.COPY
          ? false
          : question.isEditable === false ||
              (question?.hasCustomDescription &&
                !(
                  (question?.providerToDescription?.SELF || question?.description) &&
                  question?.providerToDescription?.UPWARD &&
                  question?.providerToDescription?.MANAGER &&
                  question?.providerToDescription?.PEER
                )) ||
              isInvalidQuestionDescription ||
              isInvalidVariantQuestionDescription;
      case QUESTION_SOURCES.ENGAGEMENT:
        return action === ACTIONS.COPY
          ? false
          : question?.isEditable === false ||
              !question?.description?.length ||
              !question?.tagId ||
              isInvalidQuestionDescription;
      default:
        return false;
    }
  };

  const giveTooltipText = (source) => {
    switch (source) {
      case QUESTION_SOURCES.ONE_ON_ONE:
        return 'Please add a question to save';
      case QUESTION_SOURCES.ENGAGEMENT:
        if (question?.isEditable === false) {
          return 'Please select/fill the mandatory fields';
        }
        if (isInvalidQuestionDescription) {
          return 'Character limit exceeded. Please reduce the number of characters in the question.';
        }
        break;
      case QUESTION_SOURCES.REVIEW:
        if (question?.isEditable === false)
          return 'This question is in use in active review cycles. Hence it can only be previewed and not modified';
        if (isInvalidQuestionDescription) {
          return 'Character limit exceeded. Please reduce the number of characters in the question.';
        }
        if (isInvalidVariantQuestionDescription) {
          return 'Character limit exceeded. Please reduce the number of characters in the variant questions.';
        }

        return question?.hasCustomDescription
          ? ''
          : 'This question has been used in Ongoing/Published Review Cycles and hence cannot be edited/deleted. Please use duplicate.';
      default:
        return '';
    }
  };

  return (
    <StyledDrawer
      ModalProps={{
        onBackdropClick: () => {
          handleClose();
        },
      }}
      anchor="right"
      open={display}
      loading={loading}
      onKeyDown={handleKeyDown}
      className="mesh-app-drawer"
    >
      <SidepanelHeader text={question.id ? 'Edit question' : 'Add question'} onCancel={() => handleClose()} />
      {!loading && source === QUESTION_SOURCES.ENGAGEMENT && question?.isEditable === false && !isDuplicating && (
        <Box bg="#FCE6E6" alignItems="center" padding="12px" display="flex">
          <NeedAttention mr="14px" boxSize="12px" color="#C62C02" />
          <TextLabel color="#C62C02">
            This question is being used in a survey. You cannot edit this question but you can create a duplicate of the
            question and edit it.
          </TextLabel>
        </Box>
      )}
      {loading === false ? (
        <QuestionForm
          source={source}
          question={question}
          competencies={competencies}
          drivers={drivers}
          updateQuestion={handleChange}
          setQuestion={setQuestion}
          isDuplicating={isDuplicating}
        />
      ) : (
        <CircularLoader />
      )}
      <SidepanelFooter
        disabled={getIsDisabled(source)}
        onSave={handleSave}
        onCancel={handleClose}
        loading={loading}
        tooltip={giveTooltipText(source)}
      />
    </StyledDrawer>
  );
};

const StyledDrawer = styled(Drawer)`
  position: relative;
  .MuiDrawer-paper {
    width: 40%;
  }
`;

export default QuestionDetailsSidePanel;
